import { Link } from "@/lib/router-compat";
import { useSEO } from "@/hooks/useSEO";
import { useAuth } from "@/hooks/useAuth";
import { ArrowLeft, ArrowRight, FileText, Upload, Layers, CheckCircle, AlertTriangle } from "lucide-react";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";
import { ArticleTOC } from "@/components/resources/ArticleTOC";


const sections = [
  { id: "why-summarize", title: "Why Summarize Lectures with AI?" },
  { id: "how-it-works", title: "How AI Summarization Works" },
  { id: "step-by-step", title: "Step-by-Step Process" },
  { id: "good-vs-bad", title: "Good vs Bad AI Summaries" },
  { id: "limitations", title: "Limitations & Best Practices" },
  { id: "key-takeaways", title: "Key Takeaways" },
];

export default function AISummarizeLecturesGuide() {
  const { user } = useAuth();
  useSEO({
    title: "How to Summarize Lecture Notes with AI | System Atlas",
    description: "Turn dense lecture notes and PDFs into clear, structured summaries with AI. Step-by-step guide with best practices for students.",
    type: "article",
    alternates: [
      { hreflang: "en", href: "https://systematlas.fr/learn/ai-summarize-lectures" },
      { hreflang: "fr", href: "https://systematlas.fr/fr/learn/ai-summarize-lectures" },
      { hreflang: "x-default", href: "https://systematlas.fr/learn/ai-summarize-lectures" },
    ],
  });

  return (
    <>
      <ArticleJsonLd title="How to Summarize Lecture Notes with AI" description="Step-by-step guide to AI-powered lecture summarization for students." publishedDate="2026-03-06" modifiedDate="2026-03-06" inLanguage="en" />
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/learn" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"><ArrowLeft className="w-4 h-4" />Back to guides</Link>

        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-4 tracking-tight">How to Summarize Lecture Notes with AI</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">Turning 40-page lecture PDFs into concise, structured revision sheets used to take hours. AI summarization tools do it in seconds. but the quality depends on how you use them.</p>

          <ArticleTOC sections={sections} />

          <section id="why-summarize" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-primary" />Why Summarize Lectures with AI?</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-1 shrink-0" /><span><strong>Time savings:</strong> 30 seconds vs 2+ hours of manual note-taking</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-1 shrink-0" /><span><strong>Structure:</strong> AI organizes information hierarchically. definitions, concepts, examples</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-1 shrink-0" /><span><strong>Coverage:</strong> Less likely to miss key points compared to manual highlighting</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-1 shrink-0" /><span><strong>Foundation for active study:</strong> A good summary is the starting point for quizzes and spaced review</span></li>
            </ul>
          </section>

          <section id="how-it-works" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><Layers className="w-5 h-5 text-primary" />How AI Summarization Works</h2>
            <p className="text-muted-foreground mb-4">AI summarization for study materials differs from generic text summarization. Tools like System Atlas don't just shorten text. they:</p>
            <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
              <li>Extract <strong>definitions, key concepts, and relationships</strong></li>
              <li>Organize content into a <strong>hierarchical structure</strong> (main concepts → sub-concepts → details)</li>
              <li>Preserve <strong>technical terminology</strong> and important distinctions</li>
              <li>Create a <strong>revision-ready format</strong> optimized for review and self-testing</li>
            </ol>
          </section>

          <section id="step-by-step" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><Upload className="w-5 h-5 text-primary" />Step-by-Step Process</h2>
            <div className="space-y-4">
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">1. Choose your source material</h3>
                <p className="text-sm text-muted-foreground">Lecture slides, textbook chapters, or your own notes. PDF format works best for structured content.</p>
              </div>
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">2. Upload to the AI tool</h3>
                <p className="text-sm text-muted-foreground">Drag and drop your PDF or paste text directly. System Atlas processes documents up to 50 pages.</p>
              </div>
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">3. Review the generated sheet</h3>
                <p className="text-sm text-muted-foreground">Read through the structured revision sheet. Check that key concepts are captured correctly.</p>
              </div>
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">4. Use it actively</h3>
                <p className="text-sm text-muted-foreground">Don't just read. take the auto-generated quiz, use spaced repetition, and ask questions on the content.</p>
              </div>
            </div>
          </section>

          <section id="good-vs-bad" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Good vs Bad AI Summaries</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-4 border border-primary/30 rounded-lg bg-primary/5">
                <h3 className="font-medium mb-2 text-primary">✓ Good summary</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Preserves technical terms</li>
                  <li>• Hierarchical structure</li>
                  <li>• Key definitions highlighted</li>
                  <li>• Relationships between concepts</li>
                  <li>• Exam-relevant details kept</li>
                </ul>
              </div>
              <div className="p-4 border border-destructive/30 rounded-lg bg-destructive/5">
                <h3 className="font-medium mb-2 text-destructive">✗ Bad summary</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Vague paraphrasing</li>
                  <li>• Flat list without structure</li>
                  <li>• Technical terms simplified away</li>
                  <li>• Important nuances dropped</li>
                  <li>• Too short to be useful for review</li>
                </ul>
              </div>
            </div>
          </section>

          <section id="limitations" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-primary" />Limitations & Best Practices</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li>• <strong>Always verify</strong> AI summaries against your source material. errors happen</li>
              <li>• <strong>Scanned PDFs</strong> with poor OCR quality produce worse results. use clean documents</li>
              <li>• <strong>Don't stop at the summary</strong>. use it as a starting point for active recall and quizzes</li>
              <li>• <strong>Combine sources</strong>. upload multiple lectures into the same folder for comprehensive coverage</li>
              <li>• <strong>AI works from your content</strong>. use tools that summarize your materials, not generic knowledge</li>
            </ul>
          </section>

          <section id="key-takeaways" className="mb-10 p-6 bg-muted/30 border border-border/60 rounded-xl">
            <h2 className="text-xl font-semibold mb-3">Key Takeaways</h2>
            <ul className="space-y-2 text-muted-foreground text-sm">
              <li>• AI summarization saves hours but requires clean source documents</li>
              <li>• Good summaries preserve structure, terminology, and relationships</li>
              <li>• A summary alone isn't studying. combine with quizzes and spaced repetition</li>
              <li>• Use tools that work from your own content for course-specific accuracy</li>
            </ul>
          </section>
        </article>

        {!user && (
          <section className="text-center p-8 bg-primary/5 border border-primary/15 rounded-xl mb-10">
            <h2 className="text-lg font-medium mb-2">Summarize Your First Lecture</h2>
            <p className="text-sm text-muted-foreground mb-6">Upload a PDF and get a structured revision sheet in 30 seconds. free, no account needed.</p>
            <Link to="/"><button className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">Get Started Free <ArrowRight className="w-4 h-4" /></button></Link>
          </section>
        )}

        <nav className="flex flex-col gap-2 text-sm">
          <span className="text-muted-foreground font-medium">Related guides:</span>
          <Link to="/learn/pdf-to-sheet" className="text-primary hover:underline">From PDF to Revision Sheet in Minutes →</Link>
          <Link to="/learn/revision-sheets" className="text-primary hover:underline">How to Create Effective Revision Sheets →</Link>
          <Link to="/learn/ai-quiz-generator" className="text-primary hover:underline">How to Generate Quizzes from Your Notes with AI →</Link>
        </nav>
      </div>
    </>
  );
}
